import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

// --- PC-Product Options Dynamic Price Synchronization ---
(function setupPCOptionsPriceSync() {
  function formatCurrency(cents) {
    var val = (cents / 100).toFixed(2);
    return '$' + val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function calculatePCOptionsCents() {
    var totalCents = 0;
    var form = document.querySelector('product-form-component form, form[action*="/cart/add"]');
    var scope = form || document;

    var selectedInputs = scope.querySelectorAll(
      'input:checked, select option:checked, input[type="text"]:not([value=""]), input[type="number"]:not([value=""]), textarea:not([value=""])'
    );

    selectedInputs.forEach(function (el) {
      var priceAttr =
        el.getAttribute('data-price') ||
        el.getAttribute('data-add-price') ||
        el.getAttribute('data-option-price') ||
        el.getAttribute('data-cost');

      if (!priceAttr && el.parentElement) {
        priceAttr =
          el.parentElement.getAttribute('data-price') ||
          el.parentElement.getAttribute('data-add-price');
      }

      if (priceAttr) {
        var num = parseFloat(priceAttr);
        if (!isNaN(num)) {
          if (num < 500 && priceAttr.indexOf('.') !== -1) {
            num = Math.round(num * 100);
          } else if (num < 100 && priceAttr.indexOf('.') === -1) {
            num = Math.round(num * 100);
          }
          totalCents += num;
        }
      } else {
        var text = el.textContent || '';
        var match = text.match(/\+\s*\$?([0-9]+(?:\.[0-9]{2})?)/);
        if (match && match[1]) {
          var val = Math.round(parseFloat(match[1]) * 100);
          if (!isNaN(val)) totalCents += val;
        }
      }
    });

    return totalCents;
  }

  function updatePCOptionsPrice() {
    var extraCents = calculatePCOptionsCents();
    var formattedExtra = formatCurrency(extraCents);

    // Update Customizations Total elements
    var totalEls = document.querySelectorAll(
      '.pc_options_total_amount, .pc-customizations-total-amount, .pc_total_amount, .pc_total, .pc-customizations-total, [data-pc-options-total], [data-pc-total], .pc-options-total-price'
    );

    totalEls.forEach(function (el) {
      el.textContent = formattedExtra;
    });

    // Update Main Product Price
    var mainPriceEl = document.querySelector('product-price .price');
    if (mainPriceEl) {
      if (!mainPriceEl.dataset.baseCents) {
        var txt = mainPriceEl.textContent || '';
        var match = txt.match(/\$?([0-9]+(?:\.[0-9]{2})?)/);
        if (match && match[1]) {
          mainPriceEl.dataset.baseCents = Math.round(parseFloat(match[1]) * 100);
        }
      }
      var baseCents = parseFloat(mainPriceEl.dataset.baseCents || '0');
      if (baseCents > 0) {
        mainPriceEl.textContent = formatCurrency(baseCents + extraCents);
      }
    }
  }

  var events = [
    'change',
    'input',
    'click',
    'pc:change',
    'pc:updated',
    'pc_options_updated',
    'pc_option_change',
    'product_customizer_change',
  ];

  events.forEach(function (evt) {
    document.addEventListener(evt, updatePCOptionsPrice, { passive: true });
    window.addEventListener(evt, updatePCOptionsPrice, { passive: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updatePCOptionsPrice);
  } else {
    updatePCOptionsPrice();
  }
  setTimeout(updatePCOptionsPrice, 1000);
  setTimeout(updatePCOptionsPrice, 2000);
})();
